<template>
  <section class="contact-page">
    <!-- Hero Section -->
    <div class="hero">
      <div class="hero-content">
        <h1 class="hero-title">
          {{ $t?.('contact.title') || 'Contacto' }}
        </h1>
        <p class="hero-subtitle">
          {{ $t?.('contact.subtitle') || 'Ponte en contacto connosco para calquera consulta ou información' }}
        </p>
      </div>
      <div class="hero-decoration">
        <div class="floating-note note-1">♪</div>
        <div class="floating-note note-2">♫</div>
        <div class="floating-note note-3">✉</div>
        <div class="floating-note note-4">♬</div>
        <div class="floating-note note-5">📧</div>
        <div class="floating-note note-6">♩</div>
      </div>
    </div>

    <!-- Quick Info Stats -->
    <div class="stats-section section">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-icon"><i class="fas fa-clock"></i></div>
            <div class="stat-text">{{ $t?.('contact.responseTime') || 'Resposta rápida' }}</div>
            <div class="stat-subtext">{{ $t?.('contact.responseTimeDesc') || 'Respondemos en 24-48h' }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-icon"><i class="fas fa-users"></i></div>
            <div class="stat-text">{{ $t?.('contact.community') || 'Comunidade aberta' }}</div>
            <div class="stat-subtext">{{ $t?.('contact.communityDesc') || 'Todos son benvidos' }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-icon"><i class="fas fa-music"></i></div>
            <div class="stat-text">{{ $t?.('contact.yearsMusic') || '+135 anos de música' }}</div>
            <div class="stat-subtext">{{ $t?.('contact.yearsMusicDesc') || 'Tradición dende 1890' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Content -->
    <div class="contact-content section">
      <div class="container">
        <div class="contact-grid">
          <!-- Contact Form -->
          <div class="form-container">
            <Card variant="elevated" padding="lg" class="form-card">
              <div class="form-header">
                <h2 class="form-title">{{ $t?.('contact.sendMessage') || 'Envíanos unha mensaxe' }}</h2>
                <p class="form-description">
                  {{ $t?.('contact.formDescription') || 'Completa o formulario e responderémosche o antes posible' }}
                </p>
              </div>

              <form @submit.prevent="handleSubmit" class="contact-form" novalidate>
                <!-- Name Field -->
                <div class="form-group" :class="{ 'has-error': errors.name && touched.name }">
                  <label for="name" class="form-label">
                    <i class="fas fa-user"></i>
                    {{ $t?.('contact.name') || 'Nome' }}
                    <span class="required">*</span>
                  </label>
                  <input
                    id="name"
                    v-model="form.name"
                    @blur="validateField('name')"
                    @input="clearError('name')"
                    type="text"
                    class="form-input"
                    :placeholder="$t?.('contact.namePlaceholder') || 'O teu nome'"
                    :aria-invalid="errors.name && touched.name ? 'true' : 'false'"
                    :aria-describedby="errors.name && touched.name ? 'name-error' : undefined"
                  />
                  <Transition name="fade">
                    <span v-if="errors.name && touched.name" id="name-error" class="error-message">
                      <i class="fas fa-exclamation-circle"></i>
                      {{ errors.name }}
                    </span>
                  </Transition>
                </div>

                <!-- Email Field -->
                <div class="form-group" :class="{ 'has-error': errors.email && touched.email }">
                  <label for="email" class="form-label">
                    <i class="fas fa-envelope"></i>
                    {{ $t?.('contact.email') || 'Email' }}
                    <span class="required">*</span>
                  </label>
                  <input
                    id="email"
                    v-model="form.email"
                    @blur="validateField('email')"
                    @input="clearError('email')"
                    type="email"
                    class="form-input"
                    placeholder="exemplo@email.com"
                    :aria-invalid="errors.email && touched.email ? 'true' : 'false'"
                    :aria-describedby="errors.email && touched.email ? 'email-error' : undefined"
                  />
                  <Transition name="fade">
                    <span v-if="errors.email && touched.email" id="email-error" class="error-message">
                      <i class="fas fa-exclamation-circle"></i>
                      {{ errors.email }}
                    </span>
                  </Transition>
                </div>

                <!-- Message Field -->
                <div class="form-group" :class="{ 'has-error': errors.message && touched.message }">
                  <label for="message" class="form-label">
                    <i class="fas fa-comment-dots"></i>
                    {{ $t?.('contact.message') || 'Mensaxe' }}
                    <span class="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    v-model="form.message"
                    @blur="validateField('message')"
                    @input="clearError('message')"
                    rows="6"
                    class="form-input form-textarea"
                    :placeholder="$t?.('contact.messagePlaceholder') || 'Escribe a túa mensaxe aquí...'"
                    :aria-invalid="errors.message && touched.message ? 'true' : 'false'"
                    :aria-describedby="errors.message && touched.message ? 'message-error' : undefined"
                  ></textarea>
                  <Transition name="fade">
                    <span v-if="errors.message && touched.message" id="message-error" class="error-message">
                      <i class="fas fa-exclamation-circle"></i>
                      {{ errors.message }}
                    </span>
                  </Transition>
                </div>

                <!-- Submit Button -->
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                >
                  <i class="fas fa-paper-plane"></i>
                  {{ $t?.('contact.submit') || 'Enviar Mensaxe' }}
                </Button>
                <p class="form-note">
                  <i class="fas fa-info-circle"></i>
                  {{ $t?.('contact.mailClientNote') || 'Abrirase o teu cliente de correo para enviar a mensaxe' }}
                </p>
              </form>
            </Card>
          </div>

          <!-- Contact Info Sidebar -->
          <div class="info-sidebar">
            <!-- Location -->
            <Card variant="elevated" padding="lg" class="info-card">
              <div class="info-header">
                <div class="info-icon-large">
                  <i class="fas fa-map-marker-alt"></i>
                </div>
                <h3 class="info-title">{{ $t?.('contact.address') || 'Localización' }}</h3>
              </div>
              <p class="info-text">
                Rúa Vizconde San Alberto, s/n<br>
                15200 Noia, A Coruña<br>
                Galicia, España
              </p>
            </Card>

            <!-- Email -->
            <Card variant="elevated" padding="lg" class="info-card">
              <div class="info-header">
                <div class="info-icon-large">
                  <i class="fas fa-envelope"></i>
                </div>
                <h3 class="info-title">Email</h3>
              </div>
              <p class="info-text">
                <a href="mailto:bandadenoia@gmail.com" class="info-link" @click.prevent="copyEmail">
                  bandadenoia<span class="email-separator"> [arroba] </span>gmail.com
                </a>
                <span class="email-hint">{{ $t?.('contact.emailCopyHint') || '(Click para copiar)' }}</span>
              </p>
            </Card>

            <!-- Social Networks -->
            <Card variant="elevated" padding="lg" class="info-card social-card">
              <div class="info-header">
                <div class="info-icon-large">
                  <i class="fas fa-share-alt"></i>
                </div>
                <h3 class="info-title">{{ $t?.('contact.socialNetworks') || 'Redes Sociais' }}</h3>
              </div>
              <div class="social-links">
                <a 
                  href="https://www.facebook.com/bandademusicadenoia" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="social-link"
                  aria-label="Facebook"
                >
                  <i class="fab fa-facebook-f"></i>
                  <span>Facebook</span>
                </a>
                <a 
                  href="https://www.instagram.com/bandadenoia" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="social-link"
                  aria-label="Instagram"
                >
                  <i class="fab fa-instagram"></i>
                  <span>Instagram</span>
                </a>
                <a 
                  href="https://www.youtube.com/@bandademusicadenoia" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="social-link"
                  aria-label="YouTube"
                >
                  <i class="fab fa-youtube"></i>
                  <span>YouTube</span>
                </a>
                <a 
                  href="https://x.com/bandadenoia" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="social-link"
                  aria-label="Twitter / X"
                >
                  <i class="fab fa-x-twitter"></i>
                  <span>Twitter / X</span>
                </a>
                <a 
                  href="https://www.tiktok.com/@bandadenoia" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="social-link"
                  aria-label="TikTok"
                >
                  <i class="fab fa-tiktok"></i>
                  <span>TikTok</span>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="cta-section section">
      <div class="container">
        <Card variant="elevated" padding="lg" class="cta-card">
          <div class="cta-content">
            <div class="cta-icon">
              <i class="fas fa-graduation-cap"></i>
            </div>
            <h2 class="cta-title">{{ $t?.('contact.schoolCtaTitle') || 'Interésache a Escola de Música?' }}</h2>
            <p class="cta-text">
              {{ $t?.('contact.schoolCtaText') || 'A nosa escola ofrece formación musical para todas as idades. Aprende cun profesorado experto e forma parte da canteira da banda.' }}
            </p>
            <router-link to="/school" custom v-slot="{ navigate }">
              <Button variant="primary" size="lg" @click="navigate">
                <i class="fas fa-music"></i>
                {{ $t?.('contact.schoolCtaButton') || 'Descubre a Escola' }}
              </Button>
            </router-link>
          </div>
        </Card>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, getCurrentInstance } from 'vue';
import { useHead } from '@vueuse/head';
import Card from '../ui/Card.vue';
import Button from '../ui/Button.vue';
import { useAnalytics } from '../../composables/useAnalytics'
import { buildHeadConfig, getBreadcrumbJsonLd } from '../../seo/meta';

// Access i18n
const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;

// Analytics
const { trackFormSubmit, trackLinkClick, trackEvent } = useAnalytics()

useHead(
  buildHeadConfig('contact', {
    jsonLd: [getBreadcrumbJsonLd('contact')],
  }),
)

// Form state
const form = reactive({
  name: '',
  email: '',
  message: '',
});

const errors = reactive({
        name: '',
        email: '',
        message: '',
});

const touched = reactive({
  name: false,
  email: false,
  message: false,
});

const isSubmitting = ref(false);
const showSuccess = ref(false);

// Copy email function
const copyEmail = async () => {
  const email = 'bandadenoia@gmail.com';
  const copiedMessage = $t?.('contact.emailCopied') || '✓ Email copiado ao portapapeis!';
  try {
    await navigator.clipboard.writeText(email);
    trackEvent('email_copied', 'engagement', 'Contact Page')
    alert(copiedMessage);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = email;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    alert(copiedMessage);
  }
};

// Validation functions
const validateField = (field: keyof typeof form) => {
  touched[field] = true;
  
  if (field === 'name') {
    if (!form.name.trim()) {
      errors.name = $t?.('contact.nameRequired') || 'O nome é obrigatorio';
    } else if (form.name.trim().length < 2) {
      errors.name = $t?.('contact.nameMinLength') || 'O nome debe ter polo menos 2 caracteres';
    } else {
      errors.name = '';
    }
  }
  
  if (field === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      errors.email = $t?.('contact.emailRequired') || 'O email é obrigatorio';
    } else if (!emailRegex.test(form.email)) {
      errors.email = $t?.('contact.emailInvalid') || 'Por favor, introduce un email válido';
    } else {
      errors.email = '';
    }
  }
  
  if (field === 'message') {
    if (!form.message.trim()) {
      errors.message = $t?.('contact.messageRequired') || 'A mensaxe é obrigatoria';
    } else if (form.message.trim().length < 10) {
      errors.message = $t?.('contact.messageMinLength') || 'A mensaxe debe ter polo menos 10 caracteres';
    } else {
      errors.message = '';
    }
  }
};

const clearError = (field: keyof typeof errors) => {
  if (touched[field]) {
    validateField(field);
  }
};

const validateForm = () => {
  validateField('name');
  validateField('email');
  validateField('message');
  
  return !errors.name && !errors.email && !errors.message;
};

const handleSubmit = () => {
  // Validate all fields
  Object.keys(form).forEach(field => {
    touched[field as keyof typeof touched] = true;
  });
  
  if (!validateForm()) {
    trackEvent('form_validation_error', 'error', 'contact_form')
    return;
  }
  
  // Create mailto link with form data
  const recipient = 'bandadenoia@gmail.com';
  const subject = encodeURIComponent(`Contacto desde web - ${form.name}`);
  const body = encodeURIComponent(
    `Nome: ${form.name}\n` +
    `Email: ${form.email}\n\n` +
    `Mensaxe:\n${form.message}`
  );
  
  const mailtoLink = `mailto:${recipient}?subject=${subject}&body=${body}`;
  
  // Rastrear envío exitoso
  trackFormSubmit('contact_form')
  trackEvent('contact_submitted', 'conversion', 'Contact Form', 1)
  
  // Open email client
  window.location.href = mailtoLink;
  
  // Show info message
  setTimeout(() => {
    const mailOpening = $t?.('contact.mailOpening') || '✉️ Abrindo o teu cliente de correo...';
    const mailFallback = $t?.('contact.mailFallback') || 'Se non se abre automaticamente, podes escribirnos a:';
    alert(
      mailOpening + '\n\n' +
      mailFallback + '\n' +
      'bandadenoia@gmail.com'
    );
  }, 500);
  
  // Reset form after a delay
  setTimeout(() => {
    form.name = '';
    form.email = '';
    form.message = '';
    
    Object.keys(touched).forEach(key => {
      touched[key as keyof typeof touched] = false;
    });
    
    Object.keys(errors).forEach(key => {
      errors[key as keyof typeof errors] = '';
    });
  }, 1000);
};
</script>

<style scoped>
/* Hero Section */
.hero {
  position: relative;
  min-height: 45vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-music);
  overflow: hidden;
  padding: var(--spacing-16) var(--spacing-4);
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 70% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 60%);
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 var(--spacing-4);
  width: 100%;
}

.hero-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: var(--font-weight-bold);
  color: white;
  margin-bottom: var(--spacing-4);
  font-family: var(--font-family-serif);
  line-height: 1.1;
  text-align: center;
}

.hero-subtitle {
  font-size: clamp(1.125rem, 2.5vw, 1.5rem);
  color: rgba(255, 255, 255, 0.95);
  font-weight: var(--font-weight-medium);
  text-align: center;
}

.hero-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* Floating Notes - Consistent with Home */
.floating-note {
  position: absolute;
  font-size: 2.5rem;
  color: var(--color-secondary-400);
  opacity: 0.5;
  animation: float 6s ease-in-out infinite;
}

.note-1 {
  top: 15%;
  left: 10%;
  animation-delay: 0s;
}

.note-2 {
  top: 60%;
  right: 12%;
  animation-delay: 1.5s;
}

.note-3 {
  bottom: 20%;
  left: 18%;
  animation-delay: 3s;
  font-size: 2.8rem;
}

.note-4 {
  top: 30%;
  right: 8%;
  animation-delay: 0.5s;
  font-size: 2rem;
}

.note-5 {
  top: 70%;
  left: 8%;
  animation-delay: 2s;
  font-size: 2.2rem;
}

.note-6 {
  top: 25%;
  right: 22%;
  animation-delay: 4s;
  font-size: 2rem;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
}

/* Stats Section */
.stats-section {
  padding: var(--spacing-12) 0;
  background: linear-gradient(180deg, var(--color-neutral-50) 0%, rgba(79, 148, 160, 0.06) 100%);
  position: relative;
  overflow: hidden;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-6);
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.stat-item {
  text-align: center;
  padding: var(--spacing-6);
  border-radius: var(--border-radius-xl);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-md);
  transition: all var(--duration-normal);
  border: 1px solid rgba(79, 148, 160, 0.1);
}

.stat-item:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
  border-color: rgba(79, 148, 160, 0.2);
}

.stat-icon {
  width: 64px;
  height: 64px;
  background: var(--gradient-music);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-4) auto;
  font-size: 1.5rem;
  color: white;
  box-shadow: var(--shadow-lg);
  transition: transform var(--duration-normal);
}

.stat-item:hover .stat-icon {
  transform: scale(1.1) rotate(5deg);
}

.stat-text {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin-bottom: var(--spacing-1);
  font-family: var(--font-family-serif);
}

.stat-subtext {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

/* Contact Content */
.contact-content {
  padding: var(--spacing-20) 0;
  background: var(--color-neutral-50);
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-10);
  max-width: 1400px;
  margin: 0 auto;
}

@media (min-width: 1024px) {
  .contact-grid {
    grid-template-columns: 1.5fr 1fr;
  }
}

/* Form Card */
.form-card {
  height: fit-content;
  padding: var(--spacing-6);
}

.form-header {
  margin-bottom: var(--spacing-8);
  text-align: center;
}

.form-title {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin: 0 0 var(--spacing-2) 0;
  font-family: var(--font-family-serif);
  text-align: center;
}

.form-description {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
  text-align: center;
}

/* Form Styles */
.contact-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}

.form-note {
  margin: calc(-1 * var(--spacing-3)) 0 0 0;
  padding: var(--spacing-3);
  background: rgba(79, 148, 160, 0.1);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-primary-700);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  line-height: 1.5;
}

.form-note i {
  color: var(--color-primary-500);
  flex-shrink: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

.form-label {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.form-label i {
  color: var(--color-primary-500);
  width: 16px;
}

.required {
  color: var(--color-secondary-500);
  font-weight: var(--font-weight-bold);
}

.form-input {
  padding: var(--spacing-4);
  border: 2px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  transition: all var(--duration-fast);
  font-family: var(--font-family-sans);
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 4px rgba(79, 148, 160, 0.1);
}

.form-input::placeholder {
  color: var(--color-neutral-400);
}

.form-textarea {
  resize: vertical;
  min-height: 150px;
  line-height: 1.6;
}

.form-group.has-error .form-input {
  border-color: var(--color-error);
  background: rgba(239, 68, 68, 0.02);
}

.form-group.has-error .form-input:focus {
  border-color: var(--color-error);
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.error-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

/* Info Sidebar */
.info-sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}

.info-card {
  transition: transform var(--duration-normal) var(--ease-smooth);
}

.info-card:hover {
  transform: translateY(-4px);
}

.info-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-4);
}

.info-icon-large {
  width: 56px;
  height: 56px;
  background: var(--gradient-music);
  border-radius: var(--border-radius-xl);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: var(--shadow-primary);
  flex-shrink: 0;
}

.info-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin: 0;
  font-family: var(--font-family-serif);
}

.info-text {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.8;
  font-size: var(--font-size-base);
}

.info-link {
  color: var(--color-primary-600);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: color var(--duration-fast);
}

.info-link:hover {
  color: var(--color-primary-700);
  text-decoration: underline;
}

.email-separator {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
}

.email-hint {
  display: block;
  margin-top: var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-style: italic;
}

/* Social Card */
.social-card {
  background: white;
}

.social-card .info-icon-large {
  background: var(--gradient-music);
  box-shadow: var(--shadow-primary);
}

.social-card .info-title {
  color: var(--color-primary-700);
}

.social-links {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.social-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-3) var(--spacing-4);
  background: var(--color-neutral-50);
  border-radius: var(--border-radius-lg);
  color: var(--color-primary-700);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: all var(--duration-fast);
  border: 1px solid var(--border-color-light);
}

.social-link:hover {
  background: var(--color-primary-50);
  border-color: var(--color-primary-200);
  transform: translateX(4px);
  box-shadow: var(--shadow-sm);
  color: var(--color-primary-600);
}

.social-link i {
  font-size: 1.25rem;
  width: 24px;
  text-align: center;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--duration-fast);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* CTA Section */
.cta-section {
  padding: var(--spacing-20) 0;
  background: linear-gradient(180deg, var(--color-neutral-50) 0%, rgba(79, 148, 160, 0.08) 100%);
  position: relative;
  overflow: hidden;
}

.cta-card {
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  box-shadow: var(--shadow-2xl);
}

.cta-content {
  text-align: center;
  padding: var(--spacing-4);
}

.cta-icon {
  width: 80px;
  height: 80px;
  background: var(--gradient-music);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-6) auto;
  font-size: 2rem;
  color: white;
  box-shadow: var(--shadow-xl);
}

.cta-title {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin-bottom: var(--spacing-4);
  font-family: var(--font-family-serif);
  line-height: 1.2;
}

.cta-text {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-8);
  line-height: 1.7;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .hero {
    min-height: 35vh;
  }

  .contact-grid {
    gap: var(--spacing-8);
  }

  .form-header {
    margin-bottom: var(--spacing-6);
  }

  .stats-section {
    padding: var(--spacing-10) 0;
  }

  .stat-icon {
    width: 56px;
    height: 56px;
    font-size: 1.25rem;
  }

  .floating-note {
    font-size: 1.8rem;
    opacity: 0.4;
  }

  .cta-icon {
    width: 64px;
    height: 64px;
    font-size: 1.5rem;
  }
}

/* Refresh 2026 - visual coherence with Home */
.contact-page {
  background: transparent;
}

.hero {
  min-height: 42vh;
  padding: clamp(2.6rem, 6vw, 4.8rem) var(--spacing-4);
  background:
    radial-gradient(circle at 84% 14%, rgba(219, 174, 80, 0.2), rgba(219, 174, 80, 0) 42%),
    linear-gradient(132deg, #10293b 0%, #1a3c54 52%, #2f6285 100%);
}

.hero::before {
  background: radial-gradient(circle at 18% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 45%);
}

.hero-title {
  letter-spacing: var(--letter-spacing-tight);
}

.stats-section,
.contact-content {
  background: transparent;
}

.stat-item,
.form-card,
.info-card,
.social-card,
.cta-card {
  background: var(--surface-card-strong);
  border: 1px solid rgba(16, 41, 59, 0.12);
  box-shadow: var(--shadow-xl);
}

.stat-icon,
.info-icon-large,
.cta-icon {
  background: linear-gradient(132deg, #173c56 0%, #2f6285 55%, #a77123 100%);
}

.form-input {
  background: rgba(255, 255, 255, 0.86);
  border-color: rgba(16, 41, 59, 0.2);
}

.form-input:focus {
  box-shadow: 0 0 0 4px rgba(47, 98, 133, 0.15);
}

.social-link {
  background: rgba(255, 255, 255, 0.78);
  border-color: rgba(16, 41, 59, 0.12);
}

.social-link:hover {
  background: rgba(219, 174, 80, 0.12);
  border-color: rgba(167, 113, 35, 0.32);
  color: var(--color-primary-800);
}

.cta-section {
  background: none;
}

.cta-card {
  background:
    radial-gradient(circle at 15% 14%, rgba(219, 174, 80, 0.18), rgba(219, 174, 80, 0) 45%),
    linear-gradient(140deg, #0f293b 0%, #173c56 56%, #1f584d 100%);
  border-color: rgba(255, 255, 255, 0.14);
}

.cta-title,
.cta-text {
  color: #f8f5ef;
}
</style>
